/**
 * Cookie utility functions for centralized token management
 *
 * The parent app (cenora-core-frontend) sets an `access_token_{cognito_id}` cookie
 * on the `.cenora.io` domain that all iframes can read.
 *
 * This provides a cookie-first approach with fallback to URL/SessionStorage tokens.
 */

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
}

/**
 * Get the access token from the cookie
 * This is the preferred method as cookies are always fresh from the parent app
 * @param {string} cognitoId - The cognito_id from the user object (optional, uses SessionStorage if not provided)
 * @returns {string|null} Access token or null if not found
 */
export function getAccessTokenFromCookie(cognitoId = null) {
  // If no cognitoId provided, try to get it from SessionStorage
  if (!cognitoId) {
    // Quasar SessionStorage uses __q_objt| prefix for objects
    const quasarUserKey = '__q_objt|user';
    let userStr = sessionStorage.getItem(quasarUserKey);

    // Fallback to native sessionStorage for backward compatibility
    if (!userStr) {
      userStr = sessionStorage.getItem('user');
    }

    if (userStr) {
      try {
        // Remove Quasar prefix if present
        const jsonStr = userStr.startsWith('__q_objt|') ? userStr.substring(9) : userStr;
        const user = JSON.parse(jsonStr);
        cognitoId = user.cognito_id;
      } catch (e) {
        console.warn('Failed to parse user from SessionStorage');
      }
    }
  }

  // If we have a cognitoId, use the dynamic cookie name
  if (cognitoId) {
    return getCookie(`access_token_${cognitoId}`);
  }

  // Fallback: try the old static cookie name for backward compatibility
  return getCookie('access_token');
}
